// Мастер нового отчёта: вид работы -> данные работы -> руководитель и студент (профиль) -> состав по табл. 1 СТО;
// справа - живой титул. Создаёт проект с заполненными метаданными и заготовкой структуры в правильном порядке.
import { useEffect, useRef, useState } from 'react';
import type { Ctx, DialogProps } from '../../core/registry';
import type { Meta, StudentData, WorkType } from '../../core/types';
import type { JSONContent } from '@tiptap/core';
import { newId } from '../../core/schema/util';
import { Btn, Field, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { WORK_TYPES, ELEMENTS, elementNeed, wt, AI_TEMPLATE, ZADANIE_TITLE, type ElementId } from './data';
import { TitleView } from './title';
import { KeywordsInput } from './meta';
import { loadProfile, remember, saveProfile, getProfileCached, onProfile, type Profile } from './profile';
import { readPref, writePref } from '../../core/store';

const STEPS = ['Вид работы', 'Работа', 'Руководитель и студент', 'Состав работы'];

function emptyRich(): JSONContent { return { type: 'doc', content: [{ type: 'paragraph', attrs: { id: newId() } }] }; }
function rich(lines: string[]): JSONContent { return { type: 'doc', content: lines.map((t) => ({ type: 'paragraph', attrs: { id: newId() }, content: [{ type: 'text', text: t }] })) }; }

/** Заготовка документа по выбранному составу (порядок - как в СТО). */
export function templateDoc(type: WorkType, el: Record<string, boolean>, sections: number): JSONContent {
  const w = wt(type);
  const P = () => ({ type: 'paragraph', attrs: { id: newId() } });
  const content: JSONContent[] = [];
  if (el.intro) content.push({ type: 'structHeading', attrs: { id: newId(), kind: 'intro' } }, P());
  for (let i = 0; i < Math.max(1, sections); i++) content.push({ type: 'heading', attrs: { id: newId(), level: 1 } }, P());
  if (el.concl) content.push({ type: 'structHeading', attrs: { id: newId(), kind: w.concl } }, P());
  if (el.sources) content.push({ type: 'structHeading', attrs: { id: newId(), kind: 'sources' } });
  return { type: 'doc', content };
}

function defaultSections(type: string) { return ['kursovaya', 'kp', 'practice'].includes(type) ? 2 : 1; }

function defaultElements(type: string): Record<ElementId, boolean> {
  const r = {} as Record<ElementId, boolean>;
  for (const e of ELEMENTS) r[e.id] = e.id === 'toc' || e.id === 'concl' || (e.id !== 'ai' && elementNeed(e, type) !== '');
  // все обязательные и рекомендуемые (табл. 1 СТО) отмечены одинаково; пустой список источников в отчёт не попадает
  return r;
}

export function WizardDialog({ ctx, props, close }: DialogProps<string>) {
  const [prof, setProf] = useState<Profile | null>(null);
  const [type, setType] = useState<WorkType>(() => (readPref('last.type', 'lab') || 'lab') as WorkType);
  const [f, setF] = useState({ number: '', variant: '', discipline: '', title: '', teacher: '', position: '', name: '' });
  const [practice, setPractice] = useState({ kind: '', type: '', place: '', from: '', to: '' });
  const [keywords, setKeywords] = useState<string[]>([]);
  const [student, setStudent] = useState<Meta['student'] | null>(null);
  const [saveStudent, setSaveStudent] = useState(false);
  const [el, setEl] = useState<Record<ElementId, boolean>>(() => defaultElements(type));
  const [sections, setSections] = useState(() => defaultSections(type));
  const [template, setTemplate] = useState<'standard' | 'blank'>('standard');
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const body = useRef<HTMLDivElement>(null);
  useEffect(() => {
    void loadProfile().then((p) => {
      setProf(p); setStudent({ ...p.student });
      if (p.teachers[0]) setF((x) => ({ ...x, teacher: x.teacher || p.teachers[0].name, position: x.position || p.teachers[0].position }));
    }).catch(() => undefined);
  }, []);
  const pickType = (t: WorkType) => {
    if (t === 'sheet') { void createSheet(); return; }   // чистый лист - сразу, без данных титула и состава работы
    setType(t); setEl(defaultElements(t)); setSections(defaultSections(t));
  };
  const createSheet = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await ctx.api.createProject({ name: 'Чистый лист', type: 'sheet' });
      close(r.id);
      if (props.navigate) location.hash = '#/p/' + encodeURIComponent(r.id);
    } catch (e: any) { ctx.toast(e.message, 'error', 8000); setBusy(false); }
  };
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const meta: Meta = {
    type, number: f.number.trim(), variant: f.variant.trim(), discipline: f.discipline.trim(), title: f.title.trim().replace(/\.$/, ''),
    teacher: { name: f.teacher.trim(), position: f.position.trim() }, keywords, remarks_page: !!el.remarks,
    zadanie: { enabled: !!el.zadanie, title: ZADANIE_TITLE[type] || 'Задание', doc: emptyRich() },
    referat: { enabled: !!el.referat, doc: emptyRich() }, annotation: { enabled: false, doc: emptyRich() },
    ai_usage: { enabled: !!el.ai, facts: el.ai ? rich(AI_TEMPLATE.facts) : emptyRich(), reflection: el.ai ? rich(AI_TEMPLATE.reflection) : emptyRich() },
    practice, student: student || ({} as any), options: { sources_order: 'alpha', list_punct: 'warn' },
  };
  const auto = [wt(type).name, f.number && `№ ${f.number}`, f.discipline].filter(Boolean).join(' ');
  const create = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const nm = f.name.trim() || auto || 'Новый отчёт';
      const r = await ctx.api.createProject({ name: nm, type, template: 'blank', meta: { title: meta.title, discipline: meta.discipline, number: meta.number } });
      const doc = template === 'standard' ? templateDoc(type, el, sections) : { type: 'doc', content: [{ type: 'paragraph', attrs: { id: newId() } }] };
      await ctx.api.saveProject(r.id, { meta: { ...r.report.meta, ...meta, student: student || r.report.meta.student }, doc, sources: [], baseRevision: r.report.revision });
      writePref('last.type', type);
      void remember(meta.teacher.name ? meta.teacher : null, meta.discipline);
      if (saveStudent && student) {
        // соавторы - данные этой работы: в профиль (данные по умолчанию для новых отчётов) не сохраняются
        const { coauthors: _co, ...stud } = student;
        await saveProfile({ student: stud as Meta['student'] });
      }
      close(r.id);
      if (props.navigate) location.hash = '#/p/' + encodeURIComponent(r.id);
    } catch (e: any) { ctx.toast(e.message, 'error', 8000); setBusy(false); }
  };
  const go = (i: number) => { setActive(i); body.current?.querySelector(`[data-step="${i}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }); };
  const onScroll = () => {
    const b = body.current;
    if (!b) return;
    const top = b.getBoundingClientRect().top;
    let cur = 0;
    b.querySelectorAll('[data-step]').forEach((sec, i) => { if ((sec as HTMLElement).getBoundingClientRect().top - top < 80) cur = i; });
    setActive(cur);
  };
  const onKey = (e: React.KeyboardEvent) => {
    const t = e.target as HTMLElement;
    if (e.key === 'Enter' && t.tagName === 'INPUT' && !t.closest('.kw-box')) { e.preventDefault(); void create(); }
  };
  return (
    <Modal title="Новый отчёт по СТО" onClose={() => close()} width={1160} testid="create-dialog" footer={<>
      <span className="muted small">Всё можно изменить позже - прямо на титульном листе или «СТО → Титул и данные»</span>
      <span className="topbar-spacer" />
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Создать отчёт" primary onClick={create} disabled={busy} testid="create-submit" />
    </>}>
      <div className="wizard" data-testid="wizard" onKeyDown={onKey}>
        <div className="wizard-steps">
          {STEPS.map((s, i) => (
            <button key={s} type="button" className={'wizard-step' + (i === active ? ' is-active' : '')} onClick={() => go(i)}>
              <span className="wizard-num">{i + 1}</span>{s}</button>
          ))}
          <div className="wizard-note muted small">Enter в поле - создать сразу. Данные студента - из профиля.</div>
        </div>
        <div className="wizard-body" ref={body} onScroll={onScroll}>
          <section data-step="0">
            <h4>Вид работы</h4>
            <div className="type-grid">
              {WORK_TYPES.map((t) => (
                <button key={t.id} type="button" className={'type-card' + (type === t.id ? ' is-active' : '')} onClick={() => pickType(t.id)}
                  data-testid={'type-' + t.id} aria-pressed={type === t.id}>
                  <Icon name={t.id === 'practice' ? 'student' : t.id === 'referat' || t.id === 'essay' ? 'book' : 'file'} size={18} />
                  <span className="type-name">{t.name}</span>
                  <span className="type-hint muted">{t.hint}</span>
                </button>
              ))}
            </div>
          </section>
          <section data-step="1">
            <h4>Работа</h4>
            <div className="form-grid">
              {type !== 'practice' && <>
                <Field label="Дисциплина" wide>
                  <input list="wz-disc" value={f.discipline} onChange={(e) => set('discipline', e.target.value)} data-testid="create-discipline" />
                  <datalist id="wz-disc">{(prof?.disciplines || []).map((d) => <option key={d} value={d} />)}</datalist>
                </Field>
                <Field label="Номер работы"><input value={f.number} onChange={(e) => set('number', e.target.value)} placeholder="напр. 3" data-testid="create-number" /></Field>
                <Field label="Вариант" hint="Нет варианта - оставьте пустым"><input value={f.variant} onChange={(e) => set('variant', e.target.value)} data-testid="create-variant" /></Field>
              </>}
              <Field label="Тема работы" wide hint="С прописной буквы, без точки в конце">
                <input value={f.title} onChange={(e) => set('title', e.target.value)} data-testid="create-title" />
              </Field>
              {type === 'practice' && PRACTICE_FIELDS.map(([k, l, ph]) => (
                <Field key={k} label={l} wide={k === 'place'}><input value={practice[k]} placeholder={ph} onChange={(e) => setPractice({ ...practice, [k]: e.target.value })} /></Field>
              ))}
              <Field label="Ключевые слова (5–15) - можно позже" wide><KeywordsInput value={keywords} onChange={setKeywords} testid="create-keywords" /></Field>
              <Field label="Имя файла отчёта" wide hint={`Имя в списке ваших отчётов. Пусто - «${auto || 'Новый отчёт'}»`}>
                <input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder={auto} data-testid="create-name" />
              </Field>
            </div>
          </section>
          <section data-step="2">
            <h4>Руководитель и студент</h4>
            <div className="form-grid">
              <Field label="Руководитель (Ф. И. О. полностью)">
                <input list="wz-teachers" value={f.teacher} data-testid="create-teacher" onChange={(e) => {
                  const known = prof?.teachers.find((x) => x.name === e.target.value);
                  setF((x) => ({ ...x, teacher: e.target.value, position: known ? known.position : x.position }));
                }} />
                <datalist id="wz-teachers">{(prof?.teachers || []).map((x) => <option key={x.name} value={x.name}>{x.position}</option>)}</datalist>
              </Field>
              <Field label="Должность, степень, звание" hint="Только если точно известно"><input value={f.position} onChange={(e) => set('position', e.target.value)} data-testid="create-position" /></Field>
            </div>
            {student && (
              <div className="wizard-student">
                <div className="form-grid">
                  {STUDENT_KEYS.map(([k, label, wide]) => (
                    <Field key={k} label={label} wide={wide}>
                      <input value={(student as any)[k] || ''} onChange={(e) => setStudent({ ...student, [k]: e.target.value })} data-testid={'create-st-' + k} />
                    </Field>
                  ))}
                </div>
                <label className="check small"><input type="checkbox" checked={saveStudent} onChange={(e) => setSaveStudent(e.target.checked)} /> запомнить для следующих отчётов</label>
              </div>
            )}
          </section>
          <section data-step="3">
            <h4>Состав работы (табл. 1 СТО)</h4>
            <p className="muted small">Для вида «{wt(type).name}»: <b>+</b> обязательно, <b>р</b> рекомендуется. Элементы встанут в порядке СТО.</p>
            <div className="elements">
              {ELEMENTS.map((e) => {
                const need = elementNeed(e, type);
                return (
                  <label key={e.id} className={'element-row' + (el[e.id] ? ' is-on' : '')}>
                    <input type="checkbox" checked={!!el[e.id]} disabled={e.id === 'toc'} onChange={(x) => setEl({ ...el, [e.id]: x.target.checked })} data-testid={'create-el-' + e.id} />
                    <span className={'need need-' + (need === '+' ? 'req' : need === 'р' ? 'rec' : 'opt')}>{need || '–'}</span>
                    <span className="element-title">{e.id === 'concl' ? (wt(type).concl === 'conclusions' ? 'Выводы' : 'Заключение') : e.title}</span>
                    <span className="muted small">п. {e.rule}{e.note ? ' · ' + e.note : ''}</span>
                  </label>
                );
              })}
            </div>
            <div className="form-grid">
              <Field label="Заготовка текста">
                <select value={template} onChange={(e) => setTemplate(e.target.value as any)} data-testid="create-template">
                  <option value="standard">Структура по СТО</option><option value="blank">Пустой документ</option>
                </select>
              </Field>
              {template === 'standard' && <Field label="Разделов основной части">
                <input type="number" min={1} max={9} value={sections} onChange={(e) => setSections(Math.max(1, Math.min(9, Number(e.target.value) || 1)))} />
              </Field>}
            </div>
          </section>
        </div>
        <div className="wizard-preview" title="Так будет выглядеть титульный лист">
          <div className="meta-preview-page"><TitleView m={meta} compact /></div>
          <button type="button" className="btn-mini wizard-exact" onClick={() => ctx.openDialog('sto.titlePreview', { meta })}
            title="Собрать титул в Word и показать точно, как он будет напечатан">Точный вид (Word)</button>
        </div>
      </div>
    </Modal>
  );
}

const PRACTICE_FIELDS: ['kind' | 'type' | 'place' | 'from' | 'to', string, string][] = [
  ['kind', 'Вид практики', 'учебная / производственная'], ['type', 'Тип практики', ''], ['place', 'Место прохождения', ''],
  ['from', 'Срок: с', '«01» июля 2026 г.'], ['to', 'по', '«28» июля 2026 г.']];
const STUDENT_KEYS: [string, string, boolean][] = [['student', 'Студент (Ф. И. О.)', true],
  // групповая работа: на титуле «Выполнили обучающиеся:», в реферате и свойстве «Автор» - все авторы (3.2.3, 3.12.2)
  ['coauthors', 'Соавторы (групповая работа): Ф. И. О. через запятую', true], ['group', 'Группа', false], ['course', 'Курс', false],
  ['direction_code', 'Код направления', false], ['direction_name', 'Направление', true], ['year', 'Год', false]];

/** Блок на стартовом экране: чьи данные попадут на титул новых отчётов + правка профиля. */
export function ProfileStart({ ctx }: { ctx: Ctx }) {
  const [p, setP] = useState<Profile | null>(null);
  useEffect(() => { void loadProfile().then(setP).catch(() => undefined); return onProfile(() => setP(getProfileCached())); }, []);
  if (!p) return null;
  const st = p.student;
  // пустые поля не выводим («группа , курс» и т. п.); профиль не заполнен - короткая строка с кнопкой
  const parts = [st.student, st.group && `группа ${st.group}`, st.course && `${st.course} курс`,
    st.direction_code || st.direction_name ? [st.direction_code, st.direction_name && `«${st.direction_name}»`].filter(Boolean).join(' ') : '',
    st.branch].filter(Boolean);
  if (!parts.length) {
    return (
      <div className="start-profile" data-testid="start-profile">
        <Icon name="student" size={22} />
        <div><b>Профиль для титула не заполнен.</b> <span className="muted">Ф. И. О., группа, направление и место учёбы
          попадут на титул каждого нового отчёта.</span></div>
        <Btn icon="rename" label="Заполнить" primary onClick={() => ctx.openDialog('sto.profile', {})} testid="profile-edit" />
      </div>
    );
  }
  return (
    <div className="start-profile" data-testid="start-profile">
      <Icon name="student" size={22} />
      <div>
        <b>Данные для титула:</b> {parts.join(', ')}
        <div className="muted small">Руководителей в памяти: {p.teachers.length}{p.teachers[0] ? ` (последний - ${p.teachers[0].name})` : ''} ·
          дисциплин: {p.disciplines.length}{p.saved ? '' : ' · профиль ещё не сохранён'}</div>
      </div>
      <Btn icon="rename" label="Изменить" onClick={() => ctx.openDialog('sto.profile', {})} testid="profile-edit" />
    </div>
  );
}

const PROFILE_FIELDS: [keyof StudentData, string, boolean][] = [
  ['student', 'Ф. И. О. полностью', true], ['student_short', 'Инициалы и фамилия', false], ['group', 'Группа', false], ['course', 'Курс', false],
  ['direction_code', 'Код направления', false], ['direction_name', 'Направление подготовки', true], ['university', 'Университет', true],
  ['branch', 'Филиал', true], ['institute', 'Институт / высшая школа', true], ['city', 'Город', false], ['year', 'Год', false],
];

/** Профиль: данные студента по умолчанию, запомненные руководители и дисциплины. */
export function ProfileDialog({ ctx, close }: DialogProps) {
  const [p, setP] = useState<Profile | null>(null);
  useEffect(() => { void loadProfile(true).then((x) => setP(JSON.parse(JSON.stringify(x)))).catch((e) => ctx.toast(e.message, 'error')); }, []);
  if (!p) return null;
  const save = async () => {
    try { await saveProfile({ student: p.student, teachers: p.teachers.filter((t) => t.name.trim()), disciplines: p.disciplines.filter((d) => d.trim()) }); ctx.toast('Профиль сохранён', 'ok'); close(true); }
    catch (e: any) { ctx.toast(e.message, 'error'); }
  };
  return (
    <Modal title="Профиль студента" onClose={() => close()} width={820} testid="profile-dialog" footer={<>
      <span className="muted small">Хранится в вашей учётной записи; подставляется на титул новых отчётов</span>
      <span className="topbar-spacer" />
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Сохранить" primary onClick={save} testid="profile-save" />
    </>}>
      <div className="form-grid">
        {PROFILE_FIELDS.map(([k, label, wide]) => (
          <Field key={k} label={label} wide={wide}><input value={(p.student as any)[k] || ''} onChange={(e) => setP({ ...p, student: { ...p.student, [k]: e.target.value } })}
            data-testid={'profile-' + k} /></Field>
        ))}
      </div>
      <h4>Руководители</h4>
      {p.teachers.map((t, i) => (
        <div key={i} className="row-edit">
          <input value={t.name} placeholder="Ф. И. О." onChange={(e) => setP({ ...p, teachers: p.teachers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
          <input value={t.position} placeholder="должность (если известна)" onChange={(e) => setP({ ...p, teachers: p.teachers.map((x, j) => (j === i ? { ...x, position: e.target.value } : x)) })} />
          <button type="button" className="btn-mini" onClick={() => setP({ ...p, teachers: p.teachers.filter((_, j) => j !== i) })}>Убрать</button>
        </div>
      ))}
      <button type="button" className="link-btn" onClick={() => setP({ ...p, teachers: [...p.teachers, { name: '', position: '' }] })}>+ руководитель</button>
      <h4>Дисциплины</h4>
      <textarea rows={4} value={p.disciplines.join('\n')} onChange={(e) => setP({ ...p, disciplines: e.target.value.split('\n') })} placeholder="по одной в строке" />
    </Modal>
  );
}
